// Who is signed in, and what they may see.
//
// The server decides both — this only carries the answer around the app. The
// tab list here hides what someone cannot use; it is not the lock. Every
// route checks again on the server, because a hidden button is not security.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', user: null, config: null });

  const load = useCallback(async () => {
    let config = null;
    try {
      config = (await axios.get(`${API}/api/auth/config`)).data;
    } catch (e) {
      // If we cannot even ask, assume sign-in is off rather than locking the
      // dashboard out of itself.
      config = { enabled: false, reason: 'could not reach the server' };
    }

    if (!config.enabled) {
      setState({ status: 'disabled', user: null, config });
      return;
    }

    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      setState({ status: 'in', user: data, config });
    } catch (e) {
      setState({ status: 'out', user: null, config });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const signIn = async (credential) => {
    const { data } = await axios.post(`${API}/api/auth/google`, { credential });
    // The shared key must not survive a real login: it authenticates as a
    // machine and would let anyone holding it walk straight past all of this.
    try { localStorage.removeItem('pm_api_key'); } catch (e) { /* private mode */ }
    delete axios.defaults.headers.common['x-api-key'];
    setState(s => ({ ...s, status: 'in', user: data }));
    return data;
  };

  const signOut = async () => {
    try { await axios.post(`${API}/api/auth/logout`); } catch (e) { /* going anyway */ }
    setState(s => ({ ...s, status: 'out', user: null }));
  };

  const can = (page) => {
    if (state.status === 'disabled') return true;      // sign-in not switched on yet
    if (!state.user) return false;
    if (state.user.role === 'owner') return true;
    return (state.user.pages || []).includes(page);
  };

  return (
    <AuthContext.Provider value={{ ...state, reload: load, signIn, signOut, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
